import { css } from '@emotion/css';
import { Button, type MantineTheme, Menu, useMantineTheme } from '@mantine/core';
import { modals } from '@mantine/modals';
import {
  IconArrowRight,
  IconFileDollar,
  IconFilePlus,
  IconFileTypeCsv,
  IconFileTypePdf,
  IconFileTypeXml,
  IconInvoice,
  IconListCheck,
  IconPlus,
  IconShoppingCart,
  IconUserDollar,
  IconUsersPlus,
  IconX,
} from '@tabler/icons-react';
import { i18nKeys, useTranslation } from 'locales';
import { type ProductVariables, useSaveProduct } from 'modules/Products/api';
import { TaskForm } from 'modules/Tasks';
import { useState } from 'react';
import { useHistory } from 'react-router';
import { ProductFormModal } from 'shared/forms/ProductForm/ProductFormModal';
import { useIsMobile } from 'shared/hooks/utils/use-is-mobile';
import { useCanUseManualActions } from 'shared/utils/connector';
import { creditNoteActions } from 'store/creditNote/creditnote.actions';
import { invoiceActions } from 'store/invoice/invoice.actions';

const buttonStyles = (isOpen: boolean, theme: MantineTheme) => ({
  root: {
    width: '100%',
    backgroundColor: theme.colors.gray[0],
    outline: isOpen ? `2px solid ${theme.colors.purple[4]}` : `1px solid ${theme.colors.gray[3]}`,
    outlineOffset: isOpen ? '2px' : '0px',
  },
  label: {
    fontFamily: theme.fontFamily,
    paddingLeft: 'space4',
    flexGrow: 0.8,
  },
});

const dropdownStyles = {
  dropdown: {
    minWidth: 150,
    borderRadius: 8,
    borderColor: '#EDEDED',
    marginLeft: 15,
    boxShadow: '4px 8px 12px -2px rgba(0, 0, 0, 0.12)',
  },
};

const hover = (theme: MantineTheme) => css`
  &:hover {
    background-color: ${theme.colors.purple[1]} !important;
  }
`;

export const QuickActionMenu = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const isMobile = useIsMobile();
  const theme = useMantineTheme();

  const [isOpen, setIsOpened] = useState(false);
  const hasManualActionsActive = useCanUseManualActions();
  const { saveProduct } = useSaveProduct();

  const newInvoice = () => {
    invoiceActions.detailReset();
    history.push('/invoices/new');
  };

  const newCreditNote = () => {
    creditNoteActions.detailReset();
    history.push('/credit-notes/new');
  };

  const handleAddClient = () => {
    history.push('/clients/new');
  };

  const handleCreate = (values: ProductVariables) => {
    saveProduct(values, {
      onSuccess: () => modals.closeAll(),
    });
  };

  const addProduct = () => {
    modals.open({
      size: 'md',
      title: t(i18nKeys.NEW_PRODUCT),
      centered: true,
      children: <ProductFormModal onSubmit={handleCreate} />,
    });
  };

  const addTask = () => {
    modals.open({
      title: t(i18nKeys.FORM.TASK.NEW),
      size: 'xl',
      children: <TaskForm />,
    });
  };

  return (
    <Menu
      classNames={{ item: hover(theme) }}
      styles={{
        label: { color: theme.colors.gray[6] },
        itemLabel: { color: theme.colors.gray[10] },
      }}
      opened={isOpen}
      onChange={setIsOpened}
      position={isMobile ? 'top' : 'right-end'}
    >
      <Menu.Target>
        <Button
          c="gray.6"
          size="input-md"
          styles={buttonStyles(isOpen, theme)}
          leftSection={<IconPlus size={16} style={{ color: '#666666' }} />}
          rightSection={isOpen ? <IconX size={16} /> : <IconArrowRight size={16} />}
        >
          {t(i18nKeys.NAV.QUICK_ACTION.LABEL)}
        </Button>
      </Menu.Target>

      <Menu.Dropdown styles={dropdownStyles}>
        <Menu.Label>{t(i18nKeys.NAV.QUICK_ACTION.ADD_LABEL)}</Menu.Label>
        {hasManualActionsActive && (
          <>
            <Menu.Item leftSection={<IconFileDollar size={18} stroke={1.5} />} onClick={newInvoice}>
              {t(i18nKeys.NAV.QUICK_ACTION.INVOICE)}
            </Menu.Item>
            <Menu.Item leftSection={<IconInvoice size={18} stroke={1.5} />} onClick={newCreditNote}>
              {t(i18nKeys.NAV.QUICK_ACTION.CREDIT_NOTE)}
            </Menu.Item>
            <Menu.Item
              leftSection={<IconShoppingCart size={18} stroke={1.5} />}
              onClick={addProduct}
            >
              {t(i18nKeys.NAV.QUICK_ACTION.PRODUCT)}
            </Menu.Item>
          </>
        )}
        <Menu.Item
          leftSection={<IconUserDollar size={18} stroke={1.5} />}
          onClick={handleAddClient}
        >
          {t(i18nKeys.NAV.QUICK_ACTION.CLIENT)}
        </Menu.Item>
        <Menu.Item leftSection={<IconListCheck size={18} stroke={1.5} />} onClick={addTask}>
          {t(i18nKeys.NAV.QUICK_ACTION.TASK)}
        </Menu.Item>
        <Menu.Divider c="purple.1" />
        <Menu.Label>{t(i18nKeys.NAV.QUICK_ACTION.IMPORT_LABEL)}</Menu.Label>
        {hasManualActionsActive && (
          <Menu.Sub>
            <Menu.Sub.Target>
              <Menu.Sub.Item leftSection={<IconFilePlus size={18} stroke={1.5} />}>
                {t(i18nKeys.NAV.QUICK_ACTION.IMPORT_INVOICES)}
              </Menu.Sub.Item>
            </Menu.Sub.Target>
            <Menu.Sub.Dropdown styles={dropdownStyles}>
              <Menu.Item
                onClick={() => history.push('/import/invoices/ubl')}
                leftSection={<IconFileTypeXml size={18} stroke={1.5} />}
              >
                {t(i18nKeys.INVOICING.IMPORT.UBL_INVOICES)}
              </Menu.Item>
              <Menu.Item
                onClick={() => history.push('/import/invoices/pdf')}
                leftSection={<IconFileTypePdf size={18} stroke={1.5} />}
              >
                {t(i18nKeys.INVOICING.IMPORT.PDF_INVOICES)}
              </Menu.Item>
              <Menu.Item
                onClick={() => history.push('/import/invoices/csv')}
                leftSection={<IconFileTypeCsv size={18} stroke={1.5} />}
              >
                {t(i18nKeys.INVOICING.IMPORT.CSV_INVOICES)}
              </Menu.Item>
            </Menu.Sub.Dropdown>
          </Menu.Sub>
        )}
        <Menu.Item
          onClick={() => history.push('/import/clients/csv')}
          leftSection={<IconUsersPlus size={18} stroke={1.5} />}
        >
          {t(i18nKeys.NAV.QUICK_ACTION.IMPORT_CLIENTS)}
        </Menu.Item>
      </Menu.Dropdown>
    </Menu>
  );
};
